import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Package, 
  CheckCircle, 
  Shield, 
  AlertCircle,
  Star,
  Clock
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';
import { escrowService } from '@/lib/escrow';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface OrderConfirmationProps {
  orderId: string;
  orderStatus: string;
  deliveryStatus: string;
  escrowStatus: string;
  totalAmount: number;
  isSelfDelivered?: boolean;
  onConfirm?: () => void;
}

export function OrderConfirmation({
  orderId,
  orderStatus,
  deliveryStatus,
  escrowStatus,
  totalAmount,
  isSelfDelivered = false,
  onConfirm
}: OrderConfirmationProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { toast } = useToast();

  // For self-delivered orders, buyer can confirm when order is shipped
  // For courier orders, buyer can confirm when delivered
  const canConfirmDelivery = escrowStatus === 'held' && (
    (isSelfDelivered && (orderStatus === 'shipped' || deliveryStatus === 'shipped')) ||
    (!isSelfDelivered && deliveryStatus === 'delivered')
  );

  const { user } = useAuth();
  
  const handleConfirmDelivery = async () => {
    if (!user) return;

    setIsConfirming(true);
    const success = await escrowService.confirmDelivery(orderId, user.id);
    
    if (success) {
      toast({
        title: "Delivery Confirmed",
        description: "Payment has been released to the seller",
      });
      setShowConfirmDialog(false);
      onConfirm?.();
    } else {
      toast({
        title: "Confirmation Failed",
        description: "Unable to confirm delivery. Please try again.",
        variant: "destructive"
      });
    }
    setIsConfirming(false);
  };

  const getEscrowStatusBadge = () => {
    switch (escrowStatus) {
      case 'held':
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Shield className="h-3 w-3 mr-1" />
            Protected by Escrow
          </Badge>
        );
      case 'released':
        return (
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle className="h-3 w-3 mr-1" />
            Payment Released
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Buyer Protection
            </CardTitle>
            {getEscrowStatusBadge()}
          </div>
          <CardDescription>
            Your payment is secured until you confirm delivery
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Escrow Protection Active</p>
                <p className="text-xs text-muted-foreground mt-1">
                  ₦{totalAmount.toLocaleString()} is held securely until you confirm receipt
                </p>
              </div>
            </div>
          </div>

          {canConfirmDelivery && (
            <div className="border rounded-lg p-4 space-y-3 bg-emerald-50/50">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-600" />
                <p className="font-medium">
                  {isSelfDelivered ? 'Self-Delivery Order Ready' : 'Order Delivered'}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                {isSelfDelivered 
                  ? 'The seller is delivering this order directly. Please confirm once you receive your items in good condition.'
                  : 'Please confirm that you have received your order in good condition'
                }
              </p>
              <Button 
                onClick={() => setShowConfirmDialog(true)}
                className="w-full"
                variant="gradient"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Receipt & Release Payment
              </Button>
            </div>
          )}

          {/* Show pending message for orders not yet ready for confirmation */}
          {escrowStatus === 'held' && !canConfirmDelivery && orderStatus !== 'delivered' && (
            <div className="border rounded-lg p-4 space-y-2 bg-amber-50/50">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                <p className="font-medium text-amber-900">
                  {isSelfDelivered ? 'Awaiting Self-Delivery' : 'Order In Transit'}
                </p>
              </div>
              <p className="text-sm text-amber-700">
                {isSelfDelivered 
                  ? 'The seller will deliver your order directly. You can confirm receipt once they mark it as shipped.'
                  : 'Your order is being processed. You can confirm delivery once it arrives.'
                }
              </p>
            </div>
          )}

          {escrowStatus === 'released' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-900">Payment Released</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    You have confirmed delivery and the payment has been released to the seller
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1">
              <WhatsAppIcon className="h-4 w-4 mr-2" size={16} />
              Contact Seller
            </Button>
            <Button variant="outline" size="sm" className="flex-1">
              <AlertCircle className="h-4 w-4 mr-2" />
              Report Issue
            </Button>
          </div>

          {escrowStatus === 'released' && (
            <Button variant="outline" className="w-full">
              <Star className="h-4 w-4 mr-2" />
              Rate Your Experience
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delivery</DialogTitle>
            <DialogDescription>
              By confirming delivery, you acknowledge that:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
              <p className="text-sm">You have received your order</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
              <p className="text-sm">The items are in good condition</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
              <p className="text-sm">You agree to release ₦{totalAmount.toLocaleString()} to the seller</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelivery}
              disabled={isConfirming}
              variant="gradient"
            >
              {isConfirming ? "Confirming..." : "Confirm & Release Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}